import { Tag, PackageOpen, ImageOff } from 'lucide-react'

import { ProductPromotionPopover } from './ProductPromotionPopover'

import { BadgeListPopover } from '@/app/components/ui/BadgeListPopover'
import { isPromotionPricing } from '@/app/lib/pricing-display'
import { Product } from '@/app/services/pos.service'
import { useAccessibleBranches } from '@/hooks/useAccessibleBranches'


export interface ProductCardProps {
  product: Product
  addToCart: (product: Product) => void
  currentSucursalId?: number
}

export function ProductCard({ product, addToCart, currentSucursalId }: ProductCardProps) {
  const branchesQuery = useAccessibleBranches(true)
  const branches = branchesQuery.data || []

  const stockItems = [
    { id: 'total', name: `${product.stock || 0} u.` },
    ...branches
      .filter((b) => b.id !== currentSucursalId)
      .map((b) => {
        const found = product.stocks?.find((s) => s.sucursal_id === b.id)
        return {
          id: String(b.id),
          name: `${b.name}: ${found ? found.stock : 0} u.`
        }
      })
  ]

  const activeTiers = (product.priceTiers || []).filter(t => t.isActive && (t.lifecycleStatus === 'ACTIVE' || t.lifecycleStatus === undefined));

  const tierItems = activeTiers.map(tier => {
    let dateSuffix = '';
    if (tier.endsAt) {
      const endDate = new Date(tier.endsAt);
      // Format to simple date (e.g., 21/7/2026)
      dateSuffix = ` (hasta ${endDate.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })})`;
    }
    return {
      id: `tier-${tier.id}`,
      name: `${tier.minQuantity}+ pzas: ${Number(tier.discountPercentage)}% dto.${dateSuffix}`,
      shortName: `${tier.minQuantity}+ pzas: ${Number(tier.discountPercentage)}% dto.`,
      badgeClassName: 'bg-orange-100 hover:bg-orange-200 text-orange-700 border-transparent font-semibold'
    };
  });

  const promotionRule = isPromotionPricing(product.pricing) ? product.pricing : null
  const displayPrice = Number(product.amounts?.regular_unit_total ?? product.price ?? 0)

  return (
    <div
      onClick={() => addToCart(product)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          addToCart(product)
        }
      }}
      tabIndex={0}
      className="flex h-full min-h-[380px] cursor-pointer flex-col justify-between rounded-lg border border-border bg-card p-4 text-left text-card-foreground transition-all hover:border-primary hover:shadow-md focus:border-primary focus:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
    >
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Product Image on the top left */}
        <div className="mb-4 flex justify-start">
            {product.image ? (
              <div className="flex items-center justify-center w-32 h-32 rounded-lg overflow-hidden bg-white border border-slate-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center w-32 h-32 rounded-lg overflow-hidden bg-primary/10">
                <ImageOff className="h-10 w-10 text-primary/50" />
              </div>
            )}
        </div>

        {/* Product Details */}
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="flex-1 overflow-hidden">
            <h3 className="text-sm font-semibold text-card-foreground line-clamp-2" title={product.name}>
              {product.name}
            </h3>
            <p className="mt-1.5 text-xs font-mono text-card-description truncate" title={product.code}>
              {product.code}
            </p>
          </div>
          {product.requiresPrescription && (
            <span className="shrink-0 rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700 uppercase">
              Rx
            </span>
          )}
        </div>

        <p className="mb-1.5 text-xs text-card-foreground/80 truncate">
          {product.category || 'Sin categoría'}
        </p>
        <p className="mb-4 text-xs text-card-description truncate">
          {product.department || 'Sin departamento'}
        </p>
      </div>

      <div className="mt-4 pt-3 border-t border-border">
        <div className="mb-3 flex items-center justify-between">
          <span className="font-bold text-card-foreground text-lg">
            ${displayPrice.toFixed(2)}
          </span>
        </div>

        {/* Stock and Promotions side by side */}
        <div className="flex items-start content-start gap-2 flex-wrap min-h-[64px]" onClick={(e) => e.stopPropagation()}>
          <BadgeListPopover
            items={stockItems}
            popoverTitle="Stock por Sucursal"
            icon={<PackageOpen className="h-4 w-4 shrink-0 text-card-description" />}
          />
          {promotionRule && <ProductPromotionPopover pricing={promotionRule} />}
          {tierItems.length > 0 && (
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <BadgeListPopover
                items={tierItems}
                popoverTitle="Precios por cantidad"
                icon={<Tag className="h-4 w-4 shrink-0 text-orange-400" />}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
